<template>
  <Teleport to="body">
    <div class="listing-search-sheet">
      <div class="listing-search-sheet__scrim" @click="emit('close')" />
      <div class="listing-search-sheet__sheet" role="dialog" aria-modal="true" aria-label="Zoekfilter">
        <div class="listing-search-sheet__head">
          <span>{{ t('listing-search.sheet.title') }}</span>
          <button
            ref="closeButton"
            type="button"
            class="listing-search-sheet__close"
            :aria-label="t('listing-search.sheet.close')"
            @click="emit('close')"
          >
            <SvgIcon name="x" :size="17" />
          </button>
        </div>

        <div ref="sheetScroll" class="listing-search-sheet__scroll">
          <div class="listing-search-sheet__section" :class="{ 'is-open': step === 'location' }">
            <button
              type="button"
              class="listing-search-sheet__section-head"
              :aria-expanded="step === 'location'"
              :aria-controls="step === 'location' ? 'sheet-section-location' : undefined"
              @click="toggle('location')"
            >
              <span class="listing-search-sheet__section-title">{{ t('listing-search.location.label') }}</span>
              <span v-if="step !== 'location'" class="listing-search-sheet__section-value">{{ locationLabel }}</span>
            </button>
            <div v-if="step === 'location'" id="sheet-section-location" class="listing-search-sheet__section-body">
              <input
                class="listing-search-sheet__input"
                maxlength="75"
                :placeholder="t('listing-search.location.placeholder')"
                :value="criteria.loc"
                @input="onLocationInput($event.target.value)"
                role="combobox"
                aria-autocomplete="list"
                :aria-expanded="true"
                :aria-controls="locationPopover && locationPopover.listboxId"
                :aria-activedescendant="locationPopover && locationPopover.activeId"
                @keydown.down.prevent="locationPopover && locationPopover.moveHighlight(1)"
                @keydown.up.prevent="locationPopover && locationPopover.moveHighlight(-1)"
                @keydown.enter.prevent="locationPopover && locationPopover.pickHighlighted()"
              />
              <PopoverLocation
                ref="locationPopover"
                :api="api"
                :bootstrap="bootstrap"
                skippable
                @pick-place="emit('pick-place', $event)"
                @pick-project="emit('pick-project', $event)"
                @next="advance('price', $event && $event.viaKeyboard)"
              />
            </div>
          </div>

          <div class="listing-search-sheet__section" :class="{ 'is-open': step === 'price' }">
            <button
              type="button"
              class="listing-search-sheet__section-head"
              :aria-expanded="step === 'price'"
              :aria-controls="step === 'price' ? 'sheet-section-price' : undefined"
              @click="toggle('price')"
            >
              <span class="listing-search-sheet__section-title">{{ t('listing-search.price.label') }}</span>
              <span v-if="step !== 'price'" class="listing-search-sheet__section-value">{{ priceLabel }}</span>
            </button>
            <div v-if="step === 'price'" id="sheet-section-price" class="listing-search-sheet__section-body">
              <PopoverPrice skippable @next="advance('housing')" />
            </div>
          </div>

          <div class="listing-search-sheet__section" :class="{ 'is-open': step === 'housing' }">
            <button
              ref="housingHead"
              type="button"
              class="listing-search-sheet__section-head"
              :aria-expanded="step === 'housing'"
              :aria-controls="step === 'housing' ? 'sheet-section-housing' : undefined"
              @click="toggle('housing')"
            >
              <span class="listing-search-sheet__section-title">{{ t('listing-search.housing.label') }}</span>
              <span v-if="step !== 'housing'" class="listing-search-sheet__section-value">{{ housingLabel }}</span>
            </button>
            <div v-if="step === 'housing'" id="sheet-section-housing" class="listing-search-sheet__section-body">
              <PopoverHousing :bootstrap="bootstrap" />
            </div>
          </div>
        </div>

        <div class="listing-search-sheet__footer">
          <button type="button" class="listing-search-sheet__reset" @click="emit('reset')">
            <SvgIcon name="x" :size="13" />{{ t('listing-search.sheet.clear-all') }}
          </button>
          <button type="button" class="listing-search-sheet__submit" @click="emit('search')">
            <SvgIcon name="search" :size="17" />{{ t('listing-search.submit') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import SvgIcon from './SvgIcon.vue';
import PopoverLocation from './PopoverLocation.vue';
import PopoverPrice from './PopoverPrice.vue';
import PopoverHousing from './PopoverHousing.vue';
import { useCriteria } from '../lib/useCriteria';
import { t } from '../lib/i18n';

defineProps({
  api: { type: Object, required: true },
  bootstrap: { type: Object, required: true },
  priceLabel: { type: String, required: true },
  housingLabel: { type: String, required: true }
});

const emit = defineEmits(['close', 'search', 'reset', 'pick-place', 'pick-project']);

const { criteria, update } = useCriteria();

const step = ref('location');
const closeButton = ref(null);
const locationPopover = ref(null);
const sheetScroll = ref(null);
const housingHead = ref(null);

// A typed place without coordinates filters nothing (see apiParams), so the
// summary only claims a location once a place with coordinates is picked.
const locationLabel = computed(() => {
  if (criteria.long === null || criteria.lat === null) return t('listing-search.location.anywhere');
  return criteria.radius === null ? criteria.loc : `${criteria.loc} · ${criteria.radius} km`;
});

const toggle = (section) => {
  step.value = step.value === section ? null : section;
};

// The previous step's input unmounts with its section; without moving focus
// along it would fall back to the body.
const advance = async (section, moveFocus = true) => {
  step.value = section;
  if (!moveFocus) return;
  await nextTick();
  const target =
    section === 'price' ? sheetScroll.value.querySelector('#sheet-section-price input') : housingHead.value;
  if (target) target.focus();
};

const onLocationInput = (value) => {
  update({ loc: value, long: null, lat: null });
  emit('pick-place', null);
};

const onKeydown = (event) => {
  if (event.key === 'Escape') emit('close');
};

let previousOverflow = '';

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  if (closeButton.value) closeButton.value.focus();
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
  document.body.style.overflow = previousOverflow;
});
</script>
